import { useMemo } from 'react';

import { ClaimIdChip } from 'src/components/editorial/ClaimIdChip';
import { compressionRatio, diffWords, type DiffToken } from 'src/lib/diff';
import { explainCode } from 'src/lib/diagnostics';
import { formatClock, parseTimestampSeconds } from 'src/lib/time';
import type { EditorialNormalizedClaim } from 'src/types/editorial';

/**
 * One row of "Lo dicho y lo escrito" — the answer to "what did the machine do to what the
 * person said?" Left is `evidence.excerpt`, right is `claim_text`. `normalization.changed` is
 * deliberately not consulted for the diff: it reports whether the normalizer edited the model's
 * own first pass, and is routinely `false` on a claim that was heavily compressed.
 */
export function ClaimLedgerRow({ claim, sourceUrl }: { claim: EditorialNormalizedClaim; sourceUrl?: string }) {
  const excerpt = claim.evidence.excerpt;
  const { before, after, ratio } = useMemo(
    () => ({
      ...diffWords(excerpt, claim.claim_text),
      ratio: compressionRatio(excerpt, claim.claim_text),
    }),
    [excerpt, claim.claim_text]
  );

  const warned = claim.warnings.length > 0;
  const timestamp = formatClock(claim.evidence.timestamps[0]);
  const seconds = parseTimestampSeconds(claim.evidence.timestamps[0]);

  return (
    <article className={warned ? 'claim claim--warn' : 'claim'} id={`claim-${claim.claim_id}`}>
      <div className="claim-said">
        <p className="claim-who">
          <strong>{claim.speaker.name || 'Hablante desconocido'}</strong>
          {' · '}
          {sourceUrl && seconds !== null ? (
            <a
              className="claim-time"
              href={`${sourceUrl}&t=${seconds}s`}
              target="_blank"
              rel="noreferrer noopener"
              title="Ver en YouTube desde este momento"
            >
              {timestamp}
            </a>
          ) : (
            <span className="claim-time">{timestamp}</span>
          )}
        </p>
        {/* Testimony — what came out of the broadcast, with what the model dropped tinted faint. */}
        <p className="claim-raw">
          <DiffText tokens={before} />
        </p>
      </div>

      <div className="claim-canonical">
        <p className="claim-meta">
          <ClaimIdChip claimId={claim.claim_id} />
          <span className="chip">{claim.claim_type}</span>
          <span className="chip chip--asserted">{claim.modality}</span>
        </p>
        {/* Judgment — the canonical claim the pipeline drags into the story. */}
        <p className="claim-text">
          <DiffText tokens={after} />
        </p>

        <p className="claim-foot">
          <span className="claim-pct">conserva {Math.round(ratio * 100)}% de lo dicho</span>
          {/* The chip says what it means; the raw code stays in the title for a bug report. */}
          {claim.warnings.map(warning => {
            const diagnostic = explainCode(warning);
            return (
              <span key={warning} className="chip chip--warn" title={`${warning} — ${diagnostic.what}`}>
                {diagnostic.title}
              </span>
            );
          })}
          {claim.verification_status === 'not_assessed' && <span className="chip chip--warn">SIN VERIFICAR</span>}
        </p>
      </div>
    </article>
  );
}

/** Removed text is tinted, never struck through — a strikethrough on a real quote reads as a
 *  correction to the speaker. */
function DiffText({ tokens }: { tokens: DiffToken[] }) {
  return (
    <>
      {tokens.map((token, index) => (
        <span key={index} data-diff={token.op}>
          {token.text}
          {index < tokens.length - 1 ? ' ' : ''}
        </span>
      ))}
    </>
  );
}
